<template>
    <div class="pb-12">
        <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                {{ t("vue.blacklists") }}
            </h1>
        </div>

        <div v-if="!hasReportsAccess">
            <InfoCard
                :img-src="trialEndedImg"
                img-alt="Trial ended"
                :title="t('vue.your_trial_ended')"
                :body-text="t('vue.your_trial_ended_action_step')"
                :emphasis-text="t('vue.your_trial_ended_action_step_continue')"
                :button-label="t('vue.choose_dmarceye_plan')"
                :button-href="route('billing.subscriptions.index')"
            />
        </div>

        <div v-else>
            <template v-if="ipsBlacklistedCount > 0">
                <DynamicContentTable
                    key="blacklists-table"
                    :title="t('vue.blacklist_results_title')"
                    :subtitle="
                        t('vue.blacklist_results_subtitle', {
                            count: ipsBlacklistedCount ?? 0,
                        })
                    "
                    :search-enabled="false"
                    :pagination-enabled="false"
                    :per-page-enabled="false"
                    :rows="ipsBlacklisted"
                    :columns="columns"
                    :per-page-label="t('vue.per_page')"
                    :endpoint="'/blacklists'"
                    :base-query="{}"
                    :table="props.table"
                    :row-key="(r: any) => `${r.sender}-${r.ip}`"
                >
                    <template #row="{ row: ip }">
                        <td
                            class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                        >
                            <div class="flex items-center">
                                <div
                                    class="mr-3 flex"
                                    style="width: 20px; height: 20px"
                                >
                                    <img
                                        :src="
                                            ip.sender === 'Ecomail'
                                                ? 'https://ecomail.cz/images/favicons/favicon-32x32.png'
                                                : 'https://www.google.com/s2/favicons?domain=' +
                                                  ip.sender +
                                                  '&sz=40'
                                        "
                                        :alt="ip.sender"
                                    />
                                </div>
                                {{ ip.sender }}
                            </div>
                        </td>

                        <td
                            class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                        >
                            {{ ip.ip }}
                        </td>
                        <td
                            class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                        >
                            {{ ip.count }}
                        </td>
                        <td
                            class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                        >
                            {{ formatTimestamp(ip.last_seen) }}
                        </td>
                        <td
                            class="text-md px-3 py-4 text-gray-800 whitespace-normal break-words"
                        >
                            {{ ip.result }}
                        </td>
                    </template>
                </DynamicContentTable>

                <div
                    class="rounded-md border border-blue-500 bg-blue-50 p-4 mt-[21px]"
                >
                    <div class="flex">
                        <div class="shrink-0">
                            <InformationCircleIcon
                                class="h-5 w-5 text-blue-400"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="ml-3">
                            <p class="text-sm text-green-800">
                                {{ t("vue.we_checked") }}
                                <strong
                                    >{{ ipsUsedCount }}
                                    {{ t("vue.ips_used_count") }}</strong
                                >
                                {{ t("vue.addresses_against_blacklists") }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>

            <div v-else class="mt-[42px]">
                <InfoCard
                    :img-src="noAlertsCheck"
                    img-alt="No alerts active"
                    :title="t('vue.all_ips_clean')"
                    :show-dot="false"
                    :disable-top-margin="true"
                    :custom-width="119"
                    :custom-height="119"
                >
                    <template #body>
                        <div
                            class="mx-auto mt-2 max-w-[520px] px-4 text-center leading-[1.6]"
                        >
                            <p>
                                {{ t("vue.we_checked") }}
                                <strong
                                    >{{ ipsUsedCount }}
                                    {{ t("vue.ips_used_count") }}</strong
                                >
                                {{ t("vue.addresses_against_blacklists") }}
                            </p>
                        </div>
                    </template>
                </InfoCard>
            </div>
        </div>
    </div>

    <TrialEndedPopup />
    <ExtendYourTrialPopup />
</template>

<script lang="ts" setup>
import {
    CheckCircleIcon,
    XCircleIcon,
    InformationCircleIcon,
} from "@heroicons/vue/20/solid";
import type ISelectable from "@/types/Atomic/ISelectable";
import type { PropType } from "vue";
import InfoCard from "@/components/Content/InfoCard.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import route from "ziggy-js";
import useReportsAccess from "@/composables/useReportsAccess.ts";

const noAlertsCheck = new URL(
    "/resources/js/assets/images/speech-bubble-check.svg",
    import.meta.url,
).href;

const { t } = useTranslations();
const { hasReportsAccess } = useReportsAccess();

const trialEndedImg = new URL(
    "/resources/js/assets/images/plan-calendar.svg",
    import.meta.url,
).href;

type SortDir = "asc" | "desc";
type SortBy = "sender" | "ip" | "count" | "last_seen" | "result";

type TableState = {
    q?: string | null;
    sort_by?: SortBy | null;
    sort_dir?: SortDir | null;
    per_page?: number;
    current_page?: number;
};

const props = defineProps({
    ipsUsed: { type: Array as PropType<ISelectable[]>, default: () => [] },
    ipsBlacklisted: {
        type: Array as PropType<ISelectable[]>,
        default: () => [],
    },
    ipsUsedCount: { type: Number, default: 0 },
    ipsBlacklistedCount: { type: Number, default: 0 },

    table: {
        type: Object as PropType<TableState>,
        required: true,
    },
});

function formatTimestamp(ts: string | number): string {
    const n = Number(ts);
    const date = new Date(n * 1000);
    return date.toLocaleString();
}

const columns = [
    {
        key: "sender",
        label: t("vue.sender"),
        sortable: true,
        sortKey: "sender",
    },
    { key: "ip", label: t("vue.ip"), sortable: true, sortKey: "ip" },
    {
        key: "count",
        label: t("vue.emails_seen"),
        sortable: true,
        sortKey: "count",
    },
    {
        key: "last_seen",
        label: t("vue.last_seen"),
        sortable: true,
        sortKey: "last_seen",
    },
    {
        key: "result",
        label: t("vue.blacklisted_at"),
        sortable: true,
        sortKey: "result",
    },
];
</script>
